import { useCallback, useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const IDENTITY = { scale: 1, x: 0, y: 0 };

/**
 * Wheel / pinch / drag / double-click zoom for a single element.
 * Attach `bind` to the viewport and apply `style` to the zoomed content.
 */
export default function useZoomPan({ resetKey } = {}) {
  const viewportRef = useRef(null);
  const [view, setView] = useState(IDENTITY);
  const [dragging, setDragging] = useState(false);
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  useEffect(() => setView(IDENTITY), [resetKey]);

  // Zoom so the point under (clientX, clientY) stays fixed.
  const zoomAt = useCallback((nextScale, clientX, clientY) => {
    const el = viewportRef.current;
    setView((prev) => {
      const scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
      if (scale === MIN_SCALE) return IDENTITY;
      if (!el) return { ...prev, scale };
      const rect = el.getBoundingClientRect();
      const px = (clientX ?? rect.left + rect.width / 2) - (rect.left + rect.width / 2);
      const py = (clientY ?? rect.top + rect.height / 2) - (rect.top + rect.height / 2);
      const ratio = scale / prev.scale;
      return { scale, x: px - (px - prev.x) * ratio, y: py - (py - prev.y) * ratio };
    });
  }, []);

  const zoomBy = useCallback((factor) => zoomAt(view.scale * factor), [view.scale, zoomAt]);
  const reset = useCallback(() => setView(IDENTITY), []);

  // Non-passive wheel listener so the page doesn't scroll while zooming.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setView((prev) => {
        const scale = clamp(prev.scale * Math.exp(-e.deltaY * 0.0015), MIN_SCALE, MAX_SCALE);
        if (scale === MIN_SCALE) return IDENTITY;
        const rect = el.getBoundingClientRect();
        const px = e.clientX - (rect.left + rect.width / 2);
        const py = e.clientY - (rect.top + rect.height / 2);
        const ratio = scale / prev.scale;
        return { scale, x: px - (px - prev.x) * ratio, y: py - (py - prev.y) * ratio };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      gesture.current = {
        type: "pinch",
        dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
        scale: view.scale,
      };
    } else {
      gesture.current = { type: "pan", x: e.clientX, y: e.clientY, origin: { x: view.x, y: view.y } };
      setDragging(view.scale > 1);
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.type === "pinch") {
      const pts = [...pointers.current.values()];
      if (pts.length < 2) return;
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      zoomAt(g.scale * (dist / g.dist), (pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2);
    } else if (view.scale > 1) {
      setView((prev) => ({ ...prev, x: g.origin.x + e.clientX - g.x, y: g.origin.y + e.clientY - g.y }));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      gesture.current = null;
      setDragging(false);
    }
  };

  const onDoubleClick = (e) => {
    if (view.scale > 1) reset();
    else zoomAt(2.5, e.clientX, e.clientY);
  };

  return {
    viewportRef,
    scale: view.scale,
    zoomBy,
    reset,
    bind: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onDoubleClick,
      style: {
        touchAction: "none",
        cursor: view.scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
      },
    },
    style: {
      transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
      transition: dragging || gesture.current ? "none" : "transform 160ms ease-out",
      willChange: "transform",
    },
    minScale: MIN_SCALE,
    maxScale: MAX_SCALE,
  };
}
